import { ilOffset, toTs } from './time';

export interface CalendarEvent {
  title: string;
  details: string;
  /** YYYY-MM-DD, Israel. */
  date: string;
  /** HH:MM, Israel. */
  startHm: string;
  endHm: string;
  location?: string;
}

const compact = (date: string, hm: string) => `${date.replace(/-/g, '')}T${hm.replace(':', '')}00`;

export function googleCalendarUrl(e: CalendarEvent): string {
  return (
    'https://calendar.google.com/calendar/render?action=TEMPLATE' +
    `&text=${encodeURIComponent(e.title)}` +
    `&dates=${compact(e.date, e.startHm)}/${compact(e.date, e.endHm)}` +
    '&ctz=Asia/Jerusalem' +
    `&details=${encodeURIComponent(e.details)}` +
    `&location=${encodeURIComponent(e.location ?? 'אונליין')}`
  );
}

export function outlookCalendarUrl(e: CalendarEvent): string {
  const off = ilOffset(toTs(e.date, e.startHm));
  return (
    'https://outlook.live.com/calendar/0/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent' +
    `&subject=${encodeURIComponent(e.title)}` +
    `&startdt=${encodeURIComponent(`${e.date}T${e.startHm}:00${off}`)}` +
    `&enddt=${encodeURIComponent(`${e.date}T${e.endHm}:00${off}`)}` +
    `&body=${encodeURIComponent(e.details)}` +
    `&location=${encodeURIComponent(e.location ?? 'אונליין')}`
  );
}

const icsEscape = (s: string) =>
  s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

/** RFC 5545 folding: lines over 75 octets continue on the next line after a space. */
function fold(line: string): string {
  const enc = new TextEncoder();
  const out: string[] = [];
  let cur = '';
  let bytes = 0;
  for (const ch of line) {
    const n = enc.encode(ch).length;
    const limit = out.length === 0 ? 75 : 74; // continuation lines start with a space
    if (bytes + n > limit) {
      out.push(cur);
      cur = '';
      bytes = 0;
    }
    cur += ch;
    bytes += n;
  }
  out.push(cur);
  return out.join('\r\n ');
}

const utcStamp = (ms: number) =>
  new Date(ms)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');

/** An .ics file with UTC times, so it needs no VTIMEZONE block. */
export function buildIcs(e: CalendarEvent, uid: string, nowMs: number): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Codex//Jobfair//HE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${utcStamp(nowMs)}`,
    `DTSTART:${utcStamp(toTs(e.date, e.startHm))}`,
    `DTEND:${utcStamp(toTs(e.date, e.endHm))}`,
    `SUMMARY:${icsEscape(e.title)}`,
    `DESCRIPTION:${icsEscape(e.details)}`,
    `LOCATION:${icsEscape(e.location ?? 'אונליין')}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.map(fold).join('\r\n') + '\r\n';
}
